import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

// Native-only update loader (no-op on web). This is NOT a launch splash: it stays
// hidden on normal launches and during seamless updates, and appears ONLY while a
// FORCED update (-force) is downloading and applying. The app reloads itself once
// the forced bundle is set, so the loader naturally disappears with the reload.
// If a forced update fails/times out, otaUpdater emits 'ota-forcing-cancelled' and
// the loader fades away so the user is never trapped.
const LOGO = `${process.env.PUBLIC_URL || ''}/splash-logo.png`;
const FADE_MS = 300;

export default function OtaLoader() {
  const isNative = Capacitor.isNativePlatform();
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const [percent, setPercent] = useState(null);

  useEffect(() => {
    if (!isNative) return undefined;
    let fadeTimer;

    const onForcing = () => {
      clearTimeout(fadeTimer);
      setFading(false);
      setVisible(true);
    };
    const onProgress = (e) => {
      const p = e?.detail?.percent;
      if (typeof p === 'number') setPercent(Math.max(0, Math.min(100, Math.round(p))));
    };
    const onCancelled = () => {
      setFading(true);
      fadeTimer = setTimeout(() => setVisible(false), FADE_MS);
    };

    window.addEventListener('legallumo:ota-forcing', onForcing);
    window.addEventListener('legallumo:ota-progress', onProgress);
    window.addEventListener('legallumo:ota-forcing-cancelled', onCancelled);
    return () => {
      window.removeEventListener('legallumo:ota-forcing', onForcing);
      window.removeEventListener('legallumo:ota-progress', onProgress);
      window.removeEventListener('legallumo:ota-forcing-cancelled', onCancelled);
      clearTimeout(fadeTimer);
    };
  }, [isNative]);

  if (!visible) return null;

  return (
    <div
      aria-live="polite"
      role="status"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff',
        opacity: fading ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease`,
        pointerEvents: fading ? 'none' : 'auto',
      }}
    >
      <style>{'@keyframes ll-loader-spin{to{transform:rotate(360deg)}}'}</style>
      <img
        src={LOGO}
        alt="Legal Lumo"
        style={{ width: '52%', maxWidth: 300, height: 'auto' }}
      />
      <div
        style={{
          marginTop: 28,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          color: '#4b5563',
          fontSize: 14,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <span
          style={{
            width: 18,
            height: 18,
            border: '2px solid #d1d5db',
            borderTopColor: '#4f46e5',
            borderRadius: '50%',
            display: 'inline-block',
            animation: 'll-loader-spin 0.8s linear infinite',
          }}
        />
        <span>{percent != null ? `Updating ${percent}%` : 'Updating\u2026'}</span>
      </div>
    </div>
  );
}
